import { useState } from 'react';
import StatCard from '../components/ui/StatCard';
import Avatar from '../components/ui/Avatar';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Bar from '../components/ui/Bar';

const mockUsers = [
  { id: 1, name: 'David Odenthal', email: 'david@konversion.digital', role: 'admin', tokens_used: 124500, token_limit: 500000, status: 'active', last_active: '2026-03-20' },
  { id: 2, name: 'Anna Müller', email: 'anna@konversion.digital', role: 'editor', tokens_used: 45200, token_limit: 200000, status: 'active', last_active: '2026-03-19' },
  { id: 3, name: 'Thomas Klein', email: 'thomas@konversion.digital', role: 'viewer', tokens_used: 8900, token_limit: 100000, status: 'active', last_active: '2026-03-18' },
  { id: 4, name: 'Lisa Wagner', email: 'lisa@example.de', role: 'editor', tokens_used: 0, token_limit: 200000, status: 'invited', last_active: null },
];

const roleConfig = {
  admin: { label: 'Admin', bg: '#FEE2E2', color: '#EF4444' },
  editor: { label: 'Editor', bg: '#E6F4F2', color: '#0C9488' },
  viewer: { label: 'Viewer', bg: '#DBEAFE', color: '#3B82F6' },
};

const statusConfig = {
  active: { label: 'Aktiv', bg: '#D1FAE5', color: '#10B981' },
  invited: { label: 'Eingeladen', bg: '#FEF3C7', color: '#F59E0B' },
  inactive: { label: 'Inaktiv', bg: '#F3F4F6', color: '#6B7280' },
};

export default function Users() {
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');

  const totalTokens = mockUsers.reduce((s, u) => s + u.tokens_used, 0);
  const activeCount = mockUsers.filter((u) => u.status === 'active').length;

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-[23px] font-bold mb-0.5">Benutzer</h1>
          <p className="text-gray-500 text-[13.5px]">Team-Mitglieder und Token-Verwaltung</p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90"
        >
          + Einladen
        </button>
      </div>

      {/* Stat cards */}
      <div className="flex gap-3.5 mb-5 flex-wrap">
        <StatCard label="Benutzer gesamt" value={mockUsers.length} sub={`${activeCount} aktiv`} icon="&#x2638;" iconBg="#E6F4F2" iconColor="#0C9488" />
        <StatCard label="Tokens gesamt" value={`${(totalTokens / 1000).toFixed(0)}K`} sub="Team-Verbrauch" icon="&#x25CE;" iconBg="#DBEAFE" iconColor="#3B82F6" />
        <StatCard label="Admins" value={mockUsers.filter((u) => u.role === 'admin').length} sub="" icon="&#x2605;" iconBg="#FEE2E2" iconColor="#EF4444" />
        <StatCard label="Ausstehend" value={mockUsers.filter((u) => u.status === 'invited').length} sub="Einladungen" icon="&#x2709;" iconBg="#FEF3C7" iconColor="#F59E0B" />
      </div>

      {/* User table */}
      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Benutzer</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Rolle</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Token-Verbrauch</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Status</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Letzte Aktivität</th>
            </tr>
          </thead>
          <tbody>
            {mockUsers.map((u) => {
              const role = roleConfig[u.role];
              const status = statusConfig[u.status];
              const tokenPct = u.token_limit > 0 ? (u.tokens_used / u.token_limit) * 100 : 0;
              return (
                <tr key={u.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={u.name} size={34} />
                      <div>
                        <div className="text-[13px] font-medium">{u.name}</div>
                        <div className="text-[11.5px] text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge bg={role.bg} color={role.color}>{role.label}</Badge>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="w-[140px]">
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="text-gray-600">{(u.tokens_used / 1000).toFixed(0)}K</span>
                        <span className="text-gray-400">{(u.token_limit / 1000).toFixed(0)}K</span>
                      </div>
                      <Bar pct={tokenPct} color={tokenPct > 80 ? '#EF4444' : '#0C9488'} />
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge bg={status.bg} color={status.color}>{status.label}</Badge>
                  </td>
                  <td className="px-5 py-3.5 text-[12.5px] text-gray-500">
                    {u.last_active ? new Date(u.last_active).toLocaleDateString('de') : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Invite modal */}
      {showInvite && (
        <Modal onClose={() => setShowInvite(false)}>
          <div className="w-[400px]">
            <h2 className="text-[17px] font-bold mb-1">Benutzer einladen</h2>
            <p className="text-[13px] text-gray-500 mb-5">Neues Team-Mitglied per E-Mail einladen</p>

            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">E-Mail-Adresse</label>
              <input
                type="email"
                placeholder="name@example.de"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
              />
            </div>

            <div className="mb-5">
              <label className="text-[12px] text-gray-500 block mb-2 font-medium">Rolle</label>
              <div className="flex gap-2">
                {['admin', 'editor', 'viewer'].map((r) => (
                  <button
                    key={r}
                    onClick={() => setInviteRole(r)}
                    className={`flex-1 rounded-lg py-2.5 text-[12.5px] border transition-all ${
                      inviteRole === r
                        ? 'bg-teal border-teal text-white font-semibold'
                        : 'bg-bg border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {roleConfig[r].label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2.5 justify-end">
              <button
                onClick={() => setShowInvite(false)}
                className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => { setShowInvite(false); setInviteEmail(''); }}
                className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90"
              >
                Einladung senden
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
